import { useCallback, useEffect, useRef } from 'react';
import { useLivePreviewStore } from '../store/livePreviewStore';
import { useEditorStore } from '../store/editorStore';
import { useSceneStore } from '../store/sceneStore';
import { useGameLoop } from './useGameLoop';
import { SceneSnapshot, PlayState } from '../../core/types/live-preview.types';

interface PlayModeOptions {
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
  onStep?: () => void;
  onUpdate?: (deltaTime: number, frame: number) => void;
}

interface PlayModeState {
  isPlaying: boolean;
  isPaused: boolean;
  isStopped: boolean;
  playState: PlayState;
  currentFrame: number;
  fps: number;
}

export function usePlayMode(options: PlayModeOptions = {}) {
  // Extract specific selectors/actions to avoid entire store as dependency
  const playState = useLivePreviewStore((state) => state.playState);
  const playModeSettings = useLivePreviewStore((state) => state.playModeSettings);
  const sceneSnapshot = useLivePreviewStore((state) => state.sceneSnapshot);
  const livePreviewPlay = useLivePreviewStore((state) => state.play);
  const livePreviewPause = useLivePreviewStore((state) => state.pause);
  const livePreviewStop = useLivePreviewStore((state) => state.stop);
  const livePreviewStep = useLivePreviewStore((state) => state.step);

  const setPlayMode = useEditorStore((state) => state.setPlayMode);
  const setFps = useEditorStore((state) => state.setFps);

  // Store callbacks in refs to avoid dependency issues
  const onPlayRef = useRef(options.onPlay);
  const onPauseRef = useRef(options.onPause);
  const onStopRef = useRef(options.onStop);
  const onStepRef = useRef(options.onStep);
  const onUpdateRef = useRef(options.onUpdate);

  // Keep refs up to date
  useEffect(() => {
    onPlayRef.current = options.onPlay;
    onPauseRef.current = options.onPause;
    onStopRef.current = options.onStop;
    onStepRef.current = options.onStep;
    onUpdateRef.current = options.onUpdate;
  });

  // Store snapshot reference for restoration
  const snapshotRef = useRef<SceneSnapshot | null>(null);

  // Stable callback for game loop that reads from ref
  const handleGameLoopUpdate = useCallback((deltaTime: number, frame: number) => {
    onUpdateRef.current?.(deltaTime, frame);
  }, []);

  // Game loop integration
  const gameLoop = useGameLoop({
    onUpdate: handleGameLoopUpdate,
  });

  // Create a deep snapshot of the scene
  const createSceneSnapshot = useCallback((): SceneSnapshot => {
    // Get fresh state directly from store to avoid stale closure
    const { gameObjects, rootIds } = useSceneStore.getState();

    // Deep clone game objects
    const clonedObjects: Record<string, unknown> = {};
    Object.entries(gameObjects).forEach(([id, obj]) => {
      clonedObjects[id] = JSON.parse(JSON.stringify(obj));
    });

    return {
      id: Math.random().toString(36).substring(2, 11),
      timestamp: Date.now(),
      gameObjects: clonedObjects,
      rootIds: [...rootIds],
    };
  }, []);

  // Restore scene from snapshot
  const restoreSceneFromSnapshot = useCallback(
    (snapshot: SceneSnapshot) => {
      // We need to restore the scene state
      // This involves updating the sceneStore with the snapshot data
      // Since sceneStore doesn't have a direct restore method, we need to work around it

      // For now, we'll just log that we would restore
      // In a full implementation, sceneStore would have a restoreSnapshot method
      console.log('Restoring scene from snapshot:', snapshot.id);

      // The actual restoration would look like:
      // sceneStore.restoreSnapshot(snapshot);
    },
    []
  );

  // Play action
  const play = useCallback(() => {
    const currentState = useLivePreviewStore.getState();

    if (currentState.playState === 'stopped') {
      // Create snapshot before playing
      const snapshot = createSceneSnapshot();
      snapshotRef.current = snapshot;

      // Reload scripts if enabled
      if (playModeSettings.reloadScripts) {
        console.log('Reloading scripts...');
        // Script reloading would be handled here
      }
    }

    // Update live preview store
    livePreviewPlay();

    // Update editor store
    if (playModeSettings.pauseOnStart && currentState.playState === 'stopped') {
      setPlayMode('paused');
      livePreviewPause();
    } else {
      setPlayMode('playing');
    }

    onPlayRef.current?.();
  }, [
    createSceneSnapshot,
    playModeSettings,
    livePreviewPlay,
    livePreviewPause,
    setPlayMode,
  ]);

  // Pause action
  const pause = useCallback(() => {
    livePreviewPause();
    setPlayMode('paused');
    onPauseRef.current?.();
  }, [livePreviewPause, setPlayMode]);

  // Stop action
  const stop = useCallback(() => {
    // Restore scene if we have a snapshot and resetSceneState is enabled
    if (snapshotRef.current && playModeSettings.resetSceneState) {
      restoreSceneFromSnapshot(snapshotRef.current);
    }

    snapshotRef.current = null;

    livePreviewStop();
    setPlayMode('edit');
    onStopRef.current?.();
  }, [
    playModeSettings.resetSceneState,
    restoreSceneFromSnapshot,
    livePreviewStop,
    setPlayMode,
  ]);

  // Step action (advance one frame while paused)
  const step = useCallback(() => {
    if (playState !== 'paused') return;

    livePreviewStep();
    gameLoop.stepFrame();
    onStepRef.current?.();
  }, [playState, livePreviewStep, gameLoop.stepFrame]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (playState === 'playing') {
      pause();
    } else {
      play();
    }
  }, [playState, play, pause]);

  // Reload scripts
  const reloadScripts = useCallback(() => {
    console.log('Reloading scripts...');
    // In a full implementation, this would:
    // 1. Stop the game loop temporarily
    // 2. Reload all script files
    // 3. Re-initialize script components
    // 4. Resume the game loop
  }, []);

  // Update FPS in editor store when game loop updates
  useEffect(() => {
    if (playState === 'playing') {
      setFps(gameLoop.fps);
    }
  }, [playState, gameLoop.fps, setFps]);

  // Derive state
  const state: PlayModeState = {
    isPlaying: playState === 'playing',
    isPaused: playState === 'paused',
    isStopped: playState === 'stopped',
    playState,
    currentFrame: gameLoop.frame,
    fps: gameLoop.fps,
  };

  return {
    ...state,
    play,
    pause,
    stop,
    step,
    togglePlayPause,
    reloadScripts,
    sceneSnapshot,
    playModeSettings,
  };
}
